<template>
  <Dialog :open="open" @update:open="handleClose" :title="dialogTitle" size="md">
    <div class="space-y-4">
      <!-- Confirmation Message -->
      <p class="text-sm text-gray-600 dark:text-gray-400">
        Are you sure you want to delete this {{ modelTypeLabel }}? This action cannot be undone.
      </p>

      <!-- Model Details -->
      <Card v-if="model">
        <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Model Details</h4>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Name:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ model.name }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Slug:</span>
            <code
              class="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
            >
              {{ model.slug }}
            </code>
          </div>
          <div class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Type:</span>
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                modelTypeBadgeColor,
              ]"
            >
              {{ modelTypeLabel }}
            </span>
          </div>
          <div v-if="modelType !== 'connection'" class="flex justify-between">
            <span class="text-xs text-gray-500 dark:text-gray-400">Status:</span>
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                (model as any).is_enabled
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300',
              ]"
            >
              {{ (model as any).is_enabled ? 'Enabled' : 'Disabled' }}
            </span>
          </div>
        </div>
      </Card>

      <!-- Warning Message -->
      <div
        class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-300">
              This action is permanent
            </h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-400">
              <ul class="list-disc list-inside space-y-1">
                <li>The model will be permanently deleted</li>
                <li v-if="modelType === 'asab'">
                  Any applications using this model will stop working
                </li>
                <li v-if="modelType === 'custom'">
                  The stored API key will be permanently removed
                </li>
                <li v-if="modelType === 'connection'">
                  The stored API key and provider credentials will be permanently removed
                </li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Model Warning -->
      <div
        v-if="modelType !== 'connection' && (model as any)?.is_enabled"
        class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-300">
              Active Model
            </h3>
            <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
              <p>
                This model is currently enabled. Make sure it's not being used by any active
                applications before deleting it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <Button type="button" variant="outline" @click="handleClose" :disabled="isDeleting">
          Cancel
        </Button>
        <Button type="button" variant="danger" @click="handleConfirm" :loading="isDeleting">
          {{ isDeleting ? 'Deleting...' : 'Delete Model' }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { Dialog, Button, Card } from '@asabdev/asab-ui';
  import type { CustomModelResponse, AsabModelResponse, CustomModelConnectionResponse } from '../types';

  interface Props {
    open: boolean;
    model: CustomModelResponse | AsabModelResponse | CustomModelConnectionResponse | null;
    modelType: 'custom' | 'asab' | 'connection';
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'confirm'): void;
    (e: 'cancel'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state
  const isDeleting = ref(false);

  // Computed properties
  const dialogTitle = computed(() => {
    if (props.modelType === 'connection') return 'Delete Connection';
    return props.modelType === 'custom' ? 'Delete Custom Model' : 'Delete ASAB Model';
  });

  const modelTypeLabel = computed(() => {
    if (props.modelType === 'connection') return 'Connection';
    return props.modelType === 'custom' ? 'Custom Model (BYOM)' : 'ASAB Model';
  });

  const modelTypeBadgeColor = computed(() => {
    if (props.modelType === 'connection') return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300';
    return props.modelType === 'custom'
      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
  });

  // Methods
  const handleConfirm = () => {
    isDeleting.value = true;
    emit('confirm');

    // Reset deleting state after a short delay
    // The parent component should handle closing the modal
    setTimeout(() => {
      isDeleting.value = false;
    }, 500);
  };

  const handleClose = () => {
    if (isDeleting.value) return; // Prevent closing while deleting

    isDeleting.value = false;
    emit('update:open', false);
    emit('cancel');
  };
</script>
